/**
 * AchievementCelebration — Fullscreen celebration when unlocking achievements.
 *
 * Upgrades the tiny toast to a cinematic moment:
 * - Golden rays expanding from center
 * - Achievement icon + name enlarging
 * - Confetti particles falling
 * - Screen-wide message briefly
 * - Then shrinks into a corner notification
 */

export class AchievementCelebration {
  constructor() {
    this._injectCSS();
    this._container = document.createElement('div');
    this._container.style.cssText = `
      position: fixed; top: 0; left: 0; width: 100%; height: 100%;
      pointer-events: none; z-index: 960; display: none;
      justify-content: center; align-items: center;
    `;
    document.body.appendChild(this._container);
    this._queue = [];
    this._busy = false;
  }

  _injectCSS() {
    if (document.getElementById('fw-ach-celebration-style')) return;
    const style = document.createElement('style');
    style.id = 'fw-ach-celebration-style';
    style.textContent = `
      @keyframes fw-ach-ray-grow {
        0% { transform: scale(0) rotate(0deg); opacity: 0; }
        40% { opacity: 1; }
        100% { transform: scale(3) rotate(360deg); opacity: 0; }
      }
      @keyframes fw-ach-card-pop {
        0% { transform: scale(0.3) translateY(0); opacity: 0; }
        30% { transform: scale(1.15) translateY(0); opacity: 1; }
        50% { transform: scale(1) translateY(0); }
        80% { transform: scale(1) translateY(0); opacity: 1; }
        100% { transform: scale(0.5) translateY(-300px); opacity: 0; }
      }
      @keyframes fw-ach-confetti {
        0% { transform: translateY(-20px) rotate(0deg); opacity: 1; }
        100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Trigger celebration animation.
   * @param {string} name - Achievement name
   * @param {string} desc - Description
   * @param {string} icon - Emoji icon
   */
  celebrate(name, desc, icon = '🏆') {
    this._queue.push({ name, desc, icon });
    if (!this._busy) this._playNext();
  }

  _playNext() {
    if (this._queue.length === 0) {
      this._busy = false;
      this._container.style.display = 'none';
      return;
    }
    this._busy = true;
    const { name, desc, icon } = this._queue.shift();
    this._container.innerHTML = '';
    this._container.style.display = 'flex';

    // Golden rays (radial burst)
    const rays = document.createElement('div');
    rays.style.cssText = `
      position: absolute; width: 200px; height: 200px;
      background: conic-gradient(
        rgba(255,215,0,0.4) 0deg, transparent 20deg,
        rgba(255,215,0,0.4) 40deg, transparent 60deg,
        rgba(255,215,0,0.4) 80deg, transparent 100deg,
        rgba(255,215,0,0.4) 120deg, transparent 140deg,
        rgba(255,215,0,0.4) 160deg, transparent 180deg,
        rgba(255,215,0,0.4) 200deg, transparent 220deg,
        rgba(255,215,0,0.4) 240deg, transparent 260deg,
        rgba(255,215,0,0.4) 280deg, transparent 300deg,
        rgba(255,215,0,0.4) 320deg, transparent 340deg
      );
      border-radius: 50%;
      animation: fw-ach-ray-grow 2s ease-out forwards;
    `;
    this._container.appendChild(rays);

    // Achievement card
    const card = document.createElement('div');
    card.style.cssText = `
      position: relative;
      background: linear-gradient(135deg, #3A2F10, #5A4A20, #3A2F10);
      border: 3px solid #FFD700;
      padding: 24px 40px;
      text-align: center;
      box-shadow: 0 0 40px rgba(255,215,0,0.8), inset 0 0 20px rgba(0,0,0,0.5);
      font-family: 'Courier New', monospace;
      animation: fw-ach-card-pop 3.5s ease-in-out forwards;
      min-width: 360px;
    `;
    card.innerHTML = `
      <div style="font-size:12px;color:#FFD700;letter-spacing:4px;margin-bottom:6px;">
        ★ LOGRO DESBLOQUEADO ★
      </div>
      <div style="font-size:56px;margin:8px 0;">${icon}</div>
      <div style="font-size:22px;font-weight:bold;color:#FFF;text-shadow:2px 2px 0 #000;margin-bottom:4px;">
        ${name}
      </div>
      <div style="font-size:12px;color:#CCC;margin-top:6px;">
        ${desc}
      </div>
    `;
    this._container.appendChild(card);

    // Confetti
    for (let i = 0; i < 40; i++) {
      const confetti = document.createElement('div');
      const color = ['#FFD700', '#FF6B6B', '#4ECDC4', '#95E1D3', '#FCE38A'][Math.floor(Math.random() * 5)];
      confetti.style.cssText = `
        position: absolute;
        top: 40%;
        left: ${20 + Math.random() * 60}%;
        width: ${4 + Math.random() * 6}px;
        height: ${10 + Math.random() * 10}px;
        background: ${color};
        opacity: 1;
        animation: fw-ach-confetti ${2 + Math.random() * 2}s linear forwards;
        animation-delay: ${Math.random() * 0.5}s;
      `;
      this._container.appendChild(confetti);
    }

    // Next after total duration
    setTimeout(() => this._playNext(), 3600);
  }
}

export const achievementCelebration = new AchievementCelebration();
